import { useEffect, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { BookOpen, Inbox, Search, Settings, Sparkles } from 'lucide-react'
import { clsx } from 'clsx'
import { useResolvedPaneMode, useUi } from '../store/ui'
import ChatListPage from '../pages/ChatListPage'
import { useSetupManifest } from '../lib/api'
import { useComposerDraftSync } from '../lib/composerDraftSync'

/**
 * AppShell.
 *
 * Layout matrix:
 *
 *   - Desktop (md+, ≥768px) — mouse-driven: side rail on the left, full-width
 *     content panes to the right. NO bottom nav (rail covers all dests).
 *
 *   - Z Fold open / phone (< md) — thumb-driven: NO side rail. Bottom tab
 *     bar at the bottom. When the foldable is open (≥720px = paneMode='split'),
 *     the chat-list pane sits on the left of an open chat AND the bottom
 *     bar is full-width across both panes — best of both worlds.
 *
 *   - Phone closed in a fullscreen open chat: bottom bar hidden to free
 *     thumb space (chat has its own back arrow).
 */
export default function AppShell({ children }: { children: ReactNode }) {
  useComposerDraftSync()
  const loc = useLocation()
  const pane = useResolvedPaneMode()
  const chatFullWindow = useUi((s) => s.chatFullWindow)
  const effectiveWidth = useUi((s) => s.effectiveWidth)
  const { data: setupManifest } = useSetupManifest()
  const lanesOnlyNav = setupManifest?.ui?.hide_groups === true
  const onChats = loc.pathname.startsWith('/chats')
  const onSearch = loc.pathname.startsWith('/search')
  const onFeatures = loc.pathname.startsWith('/features')
  const onSkills = loc.pathname.startsWith('/skills')
  const onSettings = loc.pathname.startsWith('/settings')
  const inOpenChat = onChats && loc.pathname.split('/').length > 2

  const isSplit = pane === 'split'
  const fullWindowActive = inOpenChat && chatFullWindow && effectiveWidth >= 560
  const hideChatChrome = fullWindowActive
  // Show the chat list as a permanent left pane when split AND we're inside
  // a specific chat. On /chats itself, the children IS the list, so don't
  // double-render.
  const showSplitLeftPane = isSplit && inOpenChat && !fullWindowActive

  // Bottom nav visibility. Phone+foldable get it. Hide ONLY when we're in
  // a fullscreen open chat (phone mode without a left list-pane visible).
  // In split mode, the chat-list pane stays beside the chat content, so
  // the bottom bar is fine — it's full-width nav, doesn't compete.
  const showBottomNav = !inOpenChat || (isSplit && !fullWindowActive)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const root = document.documentElement
    const updateViewportHeight = () => {
      const height = window.visualViewport?.height ?? window.innerHeight
      root.style.setProperty('--app-viewport-height', `${Math.round(height)}px`)
    }
    updateViewportHeight()
    window.visualViewport?.addEventListener('resize', updateViewportHeight)
    window.visualViewport?.addEventListener('scroll', updateViewportHeight)
    window.addEventListener('resize', updateViewportHeight)
    return () => {
      window.visualViewport?.removeEventListener('resize', updateViewportHeight)
      window.visualViewport?.removeEventListener('scroll', updateViewportHeight)
      window.removeEventListener('resize', updateViewportHeight)
    }
  }, [])

  const itemCls = (active: boolean) =>
    clsx(
      'grid place-items-center w-10 h-10 rounded-xl transition-colors',
      active
        ? 'bg-stone-200 dark:bg-stone-800 text-stone-900 dark:text-stone-100'
        : 'text-stone-500 dark:text-stone-400 hover:bg-stone-200/60 dark:hover:bg-stone-800/60',
    )

  return (
    <div
      className={clsx(
        'flex app-viewport w-full overflow-hidden',
        'bg-stone-50 dark:bg-stone-900 text-stone-900 dark:text-stone-100',
      )}
    >
      {/* Side rail — desktop / mouse-driven only (md+). Below md (Z Fold
          open + phone), the bottom nav at the bottom of <main> handles
          navigation; thumb-reach beats mouse-style rail at those widths. */}
      <aside
        className={clsx(
          hideChatChrome ? 'hidden' : 'hidden md:flex',
          'shrink-0 flex-col items-center gap-2',
          'border-r border-stone-200 dark:border-stone-800',
          'pt-[max(env(safe-area-inset-top),12px)]',
          'pb-[max(env(safe-area-inset-bottom),12px)]',
          'overflow-y-auto w-14',
        )}
        aria-label="Primary navigation rail"
      >
        <Link to="/chats" aria-label="Chats" data-tooltip="Chats" className={itemCls(onChats)}>
          <Inbox className="w-5 h-5" aria-hidden />
        </Link>
        {!lanesOnlyNav && (
          <>
            <Link to="/search" aria-label="Search" data-tooltip="Search" className={itemCls(onSearch)}>
              <Search className="w-5 h-5" aria-hidden />
            </Link>
            <Link to="/features" aria-label="Features" data-tooltip="Features" className={itemCls(onFeatures)}>
              <Sparkles className="w-5 h-5" aria-hidden />
            </Link>
            <Link to="/skills" aria-label="Skill index" data-tooltip="Skill index" className={itemCls(onSkills)}>
              <BookOpen className="w-5 h-5" aria-hidden />
            </Link>
          </>
        )}
        <div className="flex-1 min-h-2" />
        <Link to="/settings" aria-label="Settings" data-tooltip="Settings" className={itemCls(onSettings)}>
          <Settings className="w-5 h-5" aria-hidden />
        </Link>
      </aside>

      {/* Main content area. In split mode w/ open chat: left pane = chat
          list, right pane = the open chat (children). Otherwise: just
          children. min-h-0 on the column container is required so its
          flex-1 child can actually shrink to fit the viewport (without
          it, content taller than dvh would push the bottom nav below
          the fold). */}
      <main className="flex-1 min-w-0 min-h-0 flex flex-col">
        <div className="flex-1 min-h-0 flex">
          {showSplitLeftPane && (
            <div
              className={clsx(
                'shrink-0 w-[360px] lg:w-[380px] border-r border-stone-200 dark:border-stone-800',
                'flex flex-col min-h-0',
              )}
            >
              <ChatListPage />
            </div>
          )}
          <div
            className={clsx(
              'flex-1 min-w-0 flex flex-col min-h-0',
              hideChatChrome && [
                'fixed left-0 right-0 z-40 w-screen',
                'top-[var(--agent-banner-height,0px)]',
                'h-[calc(var(--app-viewport-height)_-_var(--agent-banner-height,0px))]',
                'bg-stone-50 dark:bg-stone-900',
              ],
            )}
          >
            {children}
          </div>
        </div>

        {/* Bottom tab bar. Visible at <md (phone + Z Fold open). Hidden
            on desktop (side rail there). In a fullscreen open chat on
            phone, hidden to free thumb space; in split mode the chat
            list pane is still visible so the bar stays.

            Sized ~20% smaller than the side rail (per Mark) — items are
            32×32 (vs 40×40 in the rail), icons 16×16 (vs 20×20), bar
            min-height 42px (vs 52px). Still hits Apple's 30pt + Google's
            48dp recommended-minimum tap targets thanks to padding. */}
        {showBottomNav && (() => {
          const compactItemCls = (active: boolean) =>
            clsx(
              'grid place-items-center w-8 h-8 rounded-lg transition-colors',
              active
                ? 'bg-stone-200 dark:bg-stone-800 text-stone-900 dark:text-stone-100'
                : 'text-stone-500 dark:text-stone-400 hover:bg-stone-200/60 dark:hover:bg-stone-800/60',
            )
          return (
            <nav
              className={clsx(
                'md:hidden shrink-0 flex justify-around items-center',
                'border-t border-stone-200 dark:border-stone-800',
                'bg-stone-50/95 dark:bg-stone-900/95 backdrop-blur',
                'px-2 py-1 min-h-[42px]',
                'pb-[max(env(safe-area-inset-bottom),4px)]',
              )}
              aria-label="Primary"
            >
              <Link to="/chats" aria-label="Chats" className={compactItemCls(onChats)}>
                <Inbox className="w-4 h-4" aria-hidden />
              </Link>
              {!lanesOnlyNav && (
                <>
                  <Link to="/search" aria-label="Search" className={compactItemCls(onSearch)}>
                    <Search className="w-4 h-4" aria-hidden />
                  </Link>
                  <Link to="/features" aria-label="Features" className={compactItemCls(onFeatures)}>
                    <Sparkles className="w-4 h-4" aria-hidden />
                  </Link>
                  <Link to="/skills" aria-label="Skill index" className={compactItemCls(onSkills)}>
                    <BookOpen className="w-4 h-4" aria-hidden />
                  </Link>
                </>
              )}
              <Link to="/settings" aria-label="Settings" className={compactItemCls(onSettings)}>
                <Settings className="w-4 h-4" aria-hidden />
              </Link>
            </nav>
          )
        })()}
      </main>
    </div>
  )
}
